import { useState, useEffect, useRef } from "react";
import { usePageContent } from "@/hooks/usePageContent";
import { usePageVisibility, PAGE_VISIBILITY_KEYS } from "@/hooks/usePageVisibility";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { settingsApi } from "@/integrations/mysql/client";
import { useQueryClient } from "@tanstack/react-query";
import { PatientJourneyManager } from "@/components/PatientJourneyManager";
import { FAQManager } from "@/components/FAQManager";
import { TestimonialsManager } from "@/components/TestimonialsManager";
import { SponsorsManager } from "@/components/SponsorsManager";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { useToast } from "@/hooks/use-toast";
import {
  Save, Loader2, Plus, Trash2, GripVertical, ChevronDown,
  Home, Sparkles, LayoutGrid, Brain, Heart, Leaf, Star, Zap, Target, Users, Award, BookOpen,
  Users2, Route, BookMarked, Camera, HelpCircle, MessageSquareQuote, Eye, EyeOff, RefreshCw,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ── Shared UI helpers ─────────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }: { value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number }) {
  return (
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none" />
  );
}

function TagEditor({ items, onChange, placeholder }: { items: string[]; onChange: (items: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  const add = () => { const t = draft.trim(); if (t && !items.includes(t)) onChange([...items, t]); setDraft(''); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[32px]">
        {items.map((item, i) => (
          <span key={i} className="flex items-center gap-1 bg-brand-primary/8 text-brand-primary text-xs font-medium px-2.5 py-1 rounded-lg">
            {item}
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-brand-primary/50 hover:text-brand-primary ml-0.5"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-gray-400 italic">Nenhum item</span>}
      </div>
      <div className="flex gap-2">
        <input type="text" value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder ?? 'Digite e pressione Enter...'}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all" />
        <button onClick={add} className="flex items-center gap-1 bg-gray-100 hover:bg-brand-primary/10 text-gray-600 hover:text-brand-primary text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </div>
    </div>
  );
}

function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3 mt-2">
      <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
      <button onClick={onSave} disabled={saving}
        className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Salvar
      </button>
    </div>
  );
}

// ── Accordion section wrapper ─────────────────────────────────────────────────

function SectionBlock({
  id, icon: Icon, title, description,
  isOpen, onToggle,
  badge, dragHandleProps, dragRef, dragStyle, dragging,
  children,
}: {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  badge?: React.ReactNode;
  dragHandleProps?: Record<string, unknown>;
  dragRef?: (node: HTMLElement | null) => void;
  dragStyle?: React.CSSProperties;
  dragging?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      id={`home-sec-${id}`}
      ref={dragRef}
      style={dragStyle}
      className={`border rounded-xl overflow-hidden transition-all ${dragging ? 'opacity-50' : ''} ${isOpen ? 'border-brand-primary/30 shadow-sm' : 'border-gray-100 bg-white'}`}
    >
      <div className="flex items-center bg-white hover:bg-gray-50/60 transition-colors">
        {dragHandleProps && (
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors pl-3 pr-1 py-4 touch-none shrink-0"
          >
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center justify-between px-4 py-3.5 text-left min-w-0"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isOpen ? 'bg-brand-primary text-white' : 'bg-brand-primary/8 text-brand-primary'}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-800">{title}</h3>
                {badge}
              </div>
              <p className="text-xs text-gray-400 truncate">{description}</p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ml-3 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-gray-100 p-4 bg-white space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

function SortableSectionBlock(props: Parameters<typeof SectionBlock>[0]) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });
  return (
    <SectionBlock
      {...props}
      dragRef={setNodeRef}
      dragStyle={{ transform: CSS.Transform.toString(transform), transition }}
      dragging={isDragging}
      dragHandleProps={{ ...attributes, ...listeners }}
    />
  );
}

// ── Visibility toggle pill ────────────────────────────────────────────────────

function VisibilityToggle({ visKey, label }: { visKey: string; label: string }) {
  const { isVisible, setVisible, isUpdating } = usePageVisibility();
  const visible = isVisible(visKey);
  return (
    <button
      onClick={() => setVisible(visKey, !visible)}
      disabled={isUpdating}
      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${visible ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
    >
      {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {visible ? `${label} visível` : `${label} oculta`}
    </button>
  );
}


// ── 1. Hero ───────────────────────────────────────────────────────────────────

function HeroContent() {
  const { get, getText, upsert, loading } = usePageContent('home');
  const { toast } = useToast();

  const [crn, setCrn]             = useState('');
  const [title1, setTitle1]       = useState('');
  const [title2, setTitle2]       = useState('');
  const [desc, setDesc]           = useState('');
  const [benefits, setBenefits]   = useState<string[]>([]);
  const [btnPrimary, setBtnPrimary]       = useState('');
  const [btnPrimaryUrl, setBtnPrimaryUrl] = useState('');
  const [btnWpp, setBtnWpp]               = useState('');
  const [statOV, setStatOV]       = useState('');
  const [statOL, setStatOL]       = useState('');
  const [wppUrl, setWppUrl]       = useState('');
  const [saving, setSaving]       = useState(false);

  const imageUrl = (get('hero', 'image')?.content_value?.url as string) ?? '';

  useEffect(() => {
    if (loading) return;
    setCrn(getText('hero', 'crn', 'Nutricionista CRN 3/63492 · 6 anos de experiência'));
    setTitle1(getText('hero', 'title_1', 'Transforme sua'));
    setTitle2(getText('hero', 'title_2', 'relação com a comida.'));
    setDesc(getText('hero', 'description', 'Acompanhamento nutricional humanizado para quem busca saúde de verdade. Sem restrições absurdas, sem culpa, com ciência e empatia.'));
    setBenefits((get('hero', 'benefits')?.content_value?.items as string[]) ?? ['Plano alimentar 100% personalizado', 'Acompanhamento online ou presencial', 'Sem dietas restritivas ou radicais']);
    setBtnPrimary(getText('hero', 'btn_primary', 'Agendar Avaliação Gratuita →'));
    setBtnPrimaryUrl(getText('hero', 'btn_primary_url', 'https://calendar.app.google/ULCXQLbU7iFtuVHh7'));
    setBtnWpp(getText('hero', 'btn_whatsapp', 'Falar no WhatsApp'));
    setStatOV(getText('hero', 'stat_offices_val', '3'));
    setStatOL(getText('hero', 'stat_offices_label', 'Consultórios\nem SP'));
    setWppUrl(getText('hero', 'whatsapp_url', 'https://wa.me/5511998874081'));
  }, [loading]);

  const handleImageChange = async (url: string) => {
    await upsert('hero', 'image', { url });
    toast({ title: 'Foto atualizada!' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('hero', 'crn', { text: crn }),
        upsert('hero', 'title_1', { text: title1 }),
        upsert('hero', 'title_2', { text: title2 }),
        upsert('hero', 'description', { text: desc }),
        upsert('hero', 'benefits', { items: benefits }),
        upsert('hero', 'btn_primary', { text: btnPrimary }),
        upsert('hero', 'btn_primary_url', { text: btnPrimaryUrl }),
        upsert('hero', 'btn_whatsapp', { text: btnWpp }),
        upsert('hero', 'stat_offices_val', { text: statOV }),
        upsert('hero', 'stat_offices_label', { text: statOL }),
        upsert('hero', 'whatsapp_url', { text: wppUrl }),
      ]);
      toast({ title: 'Hero salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Field label="Foto de destaque" hint="Foto exibida no lado direito — recomendado formato vertical">
        <div className="mt-1">
          <PhotoCropUpload value={imageUrl} onChange={handleImageChange} shape="rect" aspect={3/4} label="Foto hero" previewClassName="w-24 h-32 rounded-xl" />
        </div>
      </Field>

      <div className="grid grid-cols-1 gap-4">
        <Field label="Badge de credencial" hint='Ex: "Nutricionista CRN 3/63492 · 6 anos de experiência"'>
          <TextInput value={crn} onChange={setCrn} placeholder="CRN 3/63492 · 6 anos de experiência" maxLength={100} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Linha 1 do título (itálico)">
            <TextInput value={title1} onChange={setTitle1} placeholder="Transforme sua" maxLength={60} />
          </Field>
          <Field label="Linha 2 do título (negrito)">
            <TextInput value={title2} onChange={setTitle2} placeholder="relação com a comida." maxLength={60} />
          </Field>
        </div>
        <Field label={`Descrição (${desc.length}/300)`}>
          <TextArea value={desc} onChange={setDesc} rows={3} maxLength={300} />
        </Field>
        <Field label="Benefícios (bullets)" hint="Lista exibida abaixo da descrição">
          <TagEditor items={benefits} onChange={setBenefits} placeholder="Ex: Plano 100% personalizado" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Texto botão principal">
            <TextInput value={btnPrimary} onChange={setBtnPrimary} placeholder="Agendar Avaliação Gratuita →" maxLength={60} />
          </Field>
          <Field label="Link do botão principal">
            <TextInput value={btnPrimaryUrl} onChange={setBtnPrimaryUrl} placeholder="https://calendar.app.google/..." maxLength={300} />
          </Field>
          <Field label="Texto botão WhatsApp">
            <TextInput value={btnWpp} onChange={setBtnWpp} placeholder="Falar no WhatsApp" maxLength={40} />
          </Field>
          <Field label="URL do WhatsApp">
            <TextInput value={wppUrl} onChange={setWppUrl} placeholder="https://wa.me/55..." maxLength={100} />
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Badges sobre a foto</p>
            <span className="text-xs text-gray-400 italic">Deixe vazio para ocultar</span>
          </div>

          {/* Badge 1 — laranja (topo direito) */}
          <div className="border border-gray-100 rounded-xl p-3 space-y-2">
            <p className="text-xs font-medium text-brand-secondary flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-brand-secondary inline-block" />
              Badge laranja (topo direito)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Valor grande">
                <TextInput value={statOV} onChange={setStatOV} placeholder="Ex: 3" maxLength={20} />
              </Field>
              <Field label="Texto ao lado">
                <TextInput value={statOL} onChange={setStatOL} placeholder="Ex: Consultórios em SP" maxLength={80} />
              </Field>
            </div>
          </div>

        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Salvar Hero
      </button>
    </div>
  );
}

// ── 2. Parceiros & Patrocinadores ─────────────────────────────────────────────

function PatrocinadoresContent() {
  const { getText, upsert, loading } = usePageContent('home');
  const { toast } = useToast();
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    setLabel(getText('sponsors', 'label', 'Parceiros & Patrocinadores'));
  }, [loading]);

  const orig = getText('sponsors', 'label', 'Parceiros & Patrocinadores');
  const dirty = label !== orig;

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsert('sponsors', 'label', { text: label });
      toast({ title: 'Parceiros salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Field label="Rótulo da seção" hint="Texto pequeno exibido acima do carrossel">
        <TextInput value={label} onChange={setLabel} placeholder="Parceiros & Patrocinadores" maxLength={80} />
      </Field>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
      <div className="border-t border-gray-100 pt-4">
        <SponsorsManager />
      </div>
    </div>
  );
}

// ── 3. Jornada do Paciente ────────────────────────────────────────────────────

function JornadaContent() {
  const { getText, upsert, loading } = usePageContent('home');
  const { toast } = useToast();

  const [sectionLabel, setSectionLabel] = useState('');
  const [title, setTitle]               = useState('');
  const [subtitle, setSubtitle]         = useState('');
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (loading) return;
    setSectionLabel(getText('jornada', 'label', 'Como funciona'));
    setTitle(getText('jornada', 'title', 'Sua jornada, passo a passo'));
    setSubtitle(getText('jornada', 'subtitle', 'Um processo claro, humano e sem surpresas. Do primeiro contato até o resultado real.'));
  }, [loading]);

  const origLabel    = getText('jornada', 'label',    'Como funciona');
  const origTitle    = getText('jornada', 'title',    'Sua jornada, passo a passo');
  const origSubtitle = getText('jornada', 'subtitle', 'Um processo claro, humano e sem surpresas. Do primeiro contato até o resultado real.');
  const dirty = sectionLabel !== origLabel || title !== origTitle || subtitle !== origSubtitle;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('jornada', 'label', { text: sectionLabel }),
        upsert('jornada', 'title', { text: title }),
        upsert('jornada', 'subtitle', { text: subtitle }),
      ]);
      toast({ title: 'Jornada salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cabeçalho da seção</p>
      <Field label="Rótulo (acima do título)">
        <TextInput value={sectionLabel} onChange={setSectionLabel} placeholder="Como funciona" maxLength={60} />
      </Field>
      <Field label="Título principal">
        <TextInput value={title} onChange={setTitle} placeholder="Sua jornada, passo a passo" maxLength={100} />
      </Field>
      <Field label={`Subtítulo (${subtitle.length}/300)`}>
        <TextArea value={subtitle} onChange={setSubtitle} rows={2} maxLength={300} />
      </Field>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
      <div className="border-t border-gray-100 pt-4">
        <PatientJourneyManager />
      </div>
    </div>
  );
}

// ── 4. Diferenciais (Abordagem) ───────────────────────────────────────────────

const ICON_OPTIONS = ['Brain', 'Heart', 'Leaf', 'Sparkles', 'Star', 'Zap', 'Target', 'Users', 'Award', 'BookOpen'];
const ICON_MAP: Record<string, React.ElementType> = { Brain, Heart, Leaf, Sparkles, Star, Zap, Target, Users, Award, BookOpen };

function SortableDifferentialCard({ id, item, index, onUpdate, onDelete }: {
  id: string; item: any; index: number;
  onUpdate: (id: string, data: any) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [title, setTitle] = useState((item.content_value?.title as string) ?? '');
  const [description, setDesc] = useState((item.content_value?.description as string) ?? '');
  const [icon, setIcon] = useState((item.content_value?.icon as string) ?? 'Brain');
  const [open, setOpen] = useState(false);

  const dirty = title !== (item.content_value?.title ?? '') || description !== (item.content_value?.description ?? '') || icon !== (item.content_value?.icon ?? 'Brain');

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className={`border rounded-xl overflow-hidden ${open ? 'border-brand-primary/30' : 'border-gray-100'}`}>
      <div className="flex items-center justify-between px-4 py-3 bg-white">
        <div className="flex items-center gap-2 min-w-0">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0 touch-none">
            <GripVertical className="w-4 h-4" />
          </div>
          {(() => { const Icon = ICON_MAP[icon] ?? Brain; return <Icon className="w-4 h-4 text-brand-primary shrink-0" />; })()}
          <button onClick={() => setOpen(v => !v)} className="text-sm font-semibold text-gray-800 hover:text-brand-primary transition-colors text-left truncate">
            {title || <span className="text-gray-400 italic">Sem título</span>}
          </button>
          {dirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-400">0{index + 1}</span>
          <button onClick={() => onDelete(item.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 space-y-3">
          <Field label={`Título (${title.length}/80)`}>
            <TextInput value={title} onChange={setTitle} maxLength={80} />
          </Field>
          <Field label="Ícone">
            <div className="flex flex-wrap gap-2 pt-1">
              {ICON_OPTIONS.map(ico => {
                const Ico = ICON_MAP[ico];
                const selected = icon === ico;
                return (
                  <button
                    key={ico}
                    type="button"
                    onClick={() => setIcon(ico)}
                    title={ico}
                    className={`flex flex-col items-center gap-1 px-2.5 py-2 rounded-lg border transition-all text-xs font-medium ${
                      selected
                        ? 'border-brand-primary bg-brand-primary/8 text-brand-primary'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-brand-primary/40 hover:text-brand-primary'
                    }`}
                  >
                    <Ico className="w-4 h-4" />
                    <span>{ico}</span>
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label={`Descrição (${description.length}/300)`}>
            <TextArea value={description} onChange={setDesc} rows={3} maxLength={300} />
          </Field>
          <div className="flex justify-end">
            <button onClick={() => onUpdate(item.id, { title, description, icon })} disabled={!dirty}
              className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40">
              <Save className="w-3.5 h-3.5" /> Salvar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const DIFFERENTIAL_DEFAULTS = [
  { icon: 'Brain',    title: 'Entende o porquê da sua alimentação', description: 'Olhamos além do prato. Entendemos seus gatilhos emocionais, sua rotina e sua relação com a comida para criar uma estratégia que funcione de verdade.' },
  { icon: 'Heart',    title: 'Sem dietas radicais ou julgamentos',  description: 'Cada paciente tem sua história. Respeitamos seu ritmo e criamos um plano que cabe na sua vida, sem culpa e sem proibições desnecessárias.' },
  { icon: 'Leaf',     title: 'Ciência aplicada ao dia a dia',       description: 'Cada orientação tem base científica e é adaptada para ser prática. Você aprende a se alimentar bem de forma sustentável, não apenas durante o tratamento.' },
  { icon: 'Sparkles', title: 'Acompanhamento real e contínuo',      description: 'Não desaparecemos depois da consulta. O suporte é contínuo para garantir que você mantenha o progresso e supere os momentos difíceis.' },
];

function DiferenciaisContent() {
  const { content, loading, upsert, updateContent, createContent, deleteContent } = usePageContent('home');
  const { toast } = useToast();
  const [localItems, setLocalItems] = useState<any[] | null>(null);
  const seededRef = useRef(false);

  const [headerLabel, setHeaderLabel] = useState('');
  const [headerTitle, setHeaderTitle] = useState('');
  const [headerSub, setHeaderSub]     = useState('');
  const [headerSaving, setHeaderSaving] = useState(false);

  const items = localItems ?? content.filter(c => c.section === 'differential' && c.content_key.startsWith('item_')).sort((a, b) => a.display_order - b.display_order);

  useEffect(() => {
    if (loading) return;
    const getText = (key: string, fb: string) => (content.find(c => c.section === 'differential' && c.content_key === key)?.content_value?.text as string) ?? fb;
    setHeaderLabel(getText('header_label', 'Por que funciona'));
    setHeaderTitle(getText('header_title', 'Uma abordagem que respeita você'));
    setHeaderSub(getText('header_subtitle', 'A diferença entre mais uma dieta fracassada e uma transformação real está na abordagem.'));

    // Auto-seed defaults into DB on first load if empty
    const dbItems = content.filter(c => c.section === 'differential' && c.content_key.startsWith('item_'));
    if (dbItems.length === 0 && !seededRef.current) {
      seededRef.current = true;
      Promise.all(
        DIFFERENTIAL_DEFAULTS.map((d, idx) =>
          createContent({ page: 'home', section: 'differential', content_key: `item_${idx}`, content_value: { title: d.title, description: d.description, icon: d.icon }, is_active: true, display_order: idx })
        )
      ).then(() => toast({ title: 'Diferenciais carregados com os valores padrão!' }));
    }
  }, [loading]);

  const headerOrig = content.find(c => c.section === 'differential' && c.content_key === 'header_label');
  const headerDirty = headerLabel !== ((headerOrig?.content_value?.text as string) ?? 'Por que funciona')
    || headerTitle !== ((content.find(c => c.section === 'differential' && c.content_key === 'header_title')?.content_value?.text as string) ?? '')
    || headerSub !== ((content.find(c => c.section === 'differential' && c.content_key === 'header_subtitle')?.content_value?.text as string) ?? '');

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(items, items.findIndex(i => i.id === active.id), items.findIndex(i => i.id === over.id));
    setLocalItems(reordered);
    try {
      await Promise.all(reordered.map((it, idx) => updateContent({ id: it.id, data: { display_order: idx } })));
    } catch { setLocalItems(null); }
  };

  const handleAdd = async () => {
    const idx = items.length;
    await createContent({ page: 'home', section: 'differential', content_key: `item_${idx}`, content_value: { title: 'Novo diferencial', description: '', icon: 'Brain' }, is_active: true, display_order: idx });
    toast({ title: 'Diferencial adicionado!' });
  };

  const handleUpdate = async (id: string, data: any) => {
    await updateContent({ id, data: { content_value: data } });
    toast({ title: 'Salvo!' });
  };

  const handleDelete = async (id: string) => {
    await deleteContent(id);
    setLocalItems(null);
    toast({ title: 'Removido!' });
  };

  const handleHeaderSave = async () => {
    setHeaderSaving(true);
    await Promise.all([
      upsert('differential', 'header_label', { text: headerLabel }),
      upsert('differential', 'header_title', { text: headerTitle }),
      upsert('differential', 'header_subtitle', { text: headerSub }),
    ]);
    setHeaderSaving(false);
    toast({ title: 'Cabeçalho salvo!' });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cabeçalho da seção</p>
        <Field label="Rótulo (acima do título)"><TextInput value={headerLabel} onChange={setHeaderLabel} placeholder="Por que funciona" maxLength={60} /></Field>
        <Field label="Título principal"><TextInput value={headerTitle} onChange={setHeaderTitle} placeholder="Uma abordagem que respeita você" maxLength={100} /></Field>
        <Field label={`Subtítulo (${headerSub.length}/300)`}><TextArea value={headerSub} onChange={setHeaderSub} rows={2} maxLength={300} /></Field>
        <SaveBar dirty={headerDirty} saving={headerSaving} onSave={handleHeaderSave} />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cards de Diferenciais</p>
          <button onClick={handleAdd} className="flex items-center gap-1 bg-brand-primary/8 hover:bg-brand-primary/15 text-brand-primary text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
        <p className="text-xs text-gray-400 flex items-center gap-1"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((item, idx) => (
              <SortableDifferentialCard key={item.id} id={item.id} item={item} index={idx} onUpdate={handleUpdate} onDelete={handleDelete} />
            ))}
          </SortableContext>
        </DndContext>
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-8">Nenhum diferencial cadastrado. Clique em Adicionar.</p>}
      </div>
    </div>
  );
}

// ── 5. Blog ───────────────────────────────────────────────────────────────────

function BlogContent() {
  const { getText, upsert, loading } = usePageContent('home');
  const { toast } = useToast();

  const [label, setLabel]         = useState('');
  const [title, setTitle]         = useState('');
  const [linkText, setLinkText]   = useState('');
  const [linkUrl, setLinkUrl]     = useState('');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (loading) return;
    setLabel(getText('blog', 'label', 'Blog'));
    setTitle(getText('blog', 'title', 'Últimas do Blog'));
    setLinkText(getText('blog', 'link_text', 'Ver todos os artigos'));
    setLinkUrl(getText('blog', 'link_url', '/blog'));
  }, [loading]);

  const origLabel    = getText('blog', 'label',     'Blog');
  const origTitle    = getText('blog', 'title',     'Últimas do Blog');
  const origLinkText = getText('blog', 'link_text', 'Ver todos os artigos');
  const origLinkUrl  = getText('blog', 'link_url',  '/blog');
  const dirty = label !== origLabel || title !== origTitle || linkText !== origLinkText || linkUrl !== origLinkUrl;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('blog', 'label',     { text: label }),
        upsert('blog', 'title',     { text: title }),
        upsert('blog', 'link_text', { text: linkText }),
        upsert('blog', 'link_url',  { text: linkUrl }),
      ]);
      toast({ title: 'Blog salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cabeçalho da seção</p>
        <VisibilityToggle visKey={PAGE_VISIBILITY_KEYS.blog} label="Seção" />
      </div>
      <Field label="Rótulo (acima do título)">
        <TextInput value={label} onChange={setLabel} placeholder="Blog" maxLength={60} />
      </Field>
      <Field label="Título principal">
        <TextInput value={title} onChange={setTitle} placeholder="Últimas do Blog" maxLength={100} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Texto do link 'Ver mais'">
          <TextInput value={linkText} onChange={setLinkText} placeholder="Ver todos os artigos" maxLength={60} />
        </Field>
        <Field label="URL do link 'Ver mais'">
          <TextInput value={linkUrl} onChange={setLinkUrl} placeholder="/blog" maxLength={200} />
        </Field>
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-400">Os artigos do blog são gerenciados na seção <strong>Blog</strong> do painel.</p>
      </div>
    </div>
  );
}

// ── 6. Instagram ──────────────────────────────────────────────────────────────

function InstagramContent() {
  const { getText, upsert, loading } = usePageContent('home');
  const { getSetting, isLoading: settingsLoading } = useSiteSettings();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [label, setLabel]       = useState('');
  const [title, setTitle]       = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [profileUrl, setProfileUrl] = useState('https://www.instagram.com/brenda.nutricionista/');
  const [saving, setSaving]     = useState(false);

  // Token state
  const [token, setToken]               = useState('');
  const [tokenVisible, setTokenVisible] = useState(false);
  const [tokenSaving, setTokenSaving]   = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenSource, setTokenSource]   = useState<'db' | 'env' | null>(null);
  const [tokenUpdatedAt, setTokenUpdatedAt] = useState<string | null>(null);

  const TOKEN_EXPIRY_DAYS = 60;

  const daysInfo = (() => {
    if (!tokenUpdatedAt) return null;
    const updated = new Date(tokenUpdatedAt);
    const now = new Date();
    const daysSince = Math.floor((now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24));
    const daysLeft = TOKEN_EXPIRY_DAYS - daysSince;
    const pct = Math.max(0, Math.min(100, (daysLeft / TOKEN_EXPIRY_DAYS) * 100));
    return { daysLeft, daysSince, pct };
  })();

  useEffect(() => {
    if (loading) return;
    setLabel(getText('instagram', 'label', 'Instagram'));
    setTitle(getText('instagram', 'title', 'Últimos Eventos'));
    setSubtitle(getText('instagram', 'subtitle', 'Acompanhe meus eventos e workshops no Instagram'));
  }, [loading]);

  useEffect(() => {
    if (!settingsLoading) {
      setProfileUrl(getSetting('instagram_profile_url', 'https://www.instagram.com/brenda.nutricionista/'));
    }
  }, [settingsLoading]);

  useEffect(() => {
    settingsApi.instagramToken()
      .then(d => {
        setToken(d.token ?? '');
        setTokenSource(d.source ?? null);
        setTokenUpdatedAt(d.updated_at ?? null);
      })
      .catch(() => {})
      .finally(() => setTokenLoading(false));
  }, []);

  const origLabel    = getText('instagram', 'label',    'Instagram');
  const origTitle    = getText('instagram', 'title',    'Últimos Eventos');
  const origSubtitle = getText('instagram', 'subtitle', 'Acompanhe meus eventos e workshops no Instagram');
  const origProfileUrl = getSetting('instagram_profile_url', 'https://www.instagram.com/brenda.nutricionista/');
  const dirty = label !== origLabel || title !== origTitle || subtitle !== origSubtitle || profileUrl !== origProfileUrl;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('instagram', 'label',    { text: label }),
        upsert('instagram', 'title',    { text: title }),
        upsert('instagram', 'subtitle', { text: subtitle }),
        settingsApi.update('instagram_profile_url', profileUrl.trim()),
      ]);
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({ title: 'Instagram salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTokenSave = async () => {
    if (!token.trim()) return;
    setTokenSaving(true);
    try {
      const now = new Date().toISOString();
      await Promise.all([
        settingsApi.update('instagram_access_token', token.trim()),
        settingsApi.update('instagram_token_updated_at', now),
      ]);
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      setTokenSource('db');
      setTokenUpdatedAt(now);
      toast({ title: 'Token atualizado! Os posts serão recarregados automaticamente.' });
    } catch {
      toast({ title: 'Erro ao salvar token', variant: 'destructive' });
    } finally {
      setTokenSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Visibilidade e perfil</p>
        <VisibilityToggle visKey={PAGE_VISIBILITY_KEYS.instagram} label="Seção" />
      </div>
      <Field label="Link do perfil do Instagram" hint="Cole o link do perfil que será aberto pelos botões. Para carregar publicações dessa conta, atualize também o token abaixo.">
        <TextInput value={profileUrl} onChange={setProfileUrl} placeholder="https://www.instagram.com/seu.perfil/" maxLength={300} />
      </Field>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cabeçalho da seção</p>
      <Field label="Rótulo (acima do título)">
        <TextInput value={label} onChange={setLabel} placeholder="Instagram" maxLength={60} />
      </Field>
      <Field label="Título principal">
        <TextInput value={title} onChange={setTitle} placeholder="Últimos Eventos" maxLength={100} />
      </Field>
      <Field label={`Subtítulo (${subtitle.length}/200)`}>
        <TextInput value={subtitle} onChange={setSubtitle} placeholder="Acompanhe meus eventos e workshops no Instagram" maxLength={200} />
      </Field>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />

      {/* ── Token ─────────────────────────────────── */}
      <div className="border-t border-gray-100 pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Token de Acesso</p>
          <div className="flex items-center gap-2">
            {tokenSource === 'db' && (
              <span className="text-xs bg-green-50 text-green-700 font-medium px-2 py-0.5 rounded-lg">Salvo no painel</span>
            )}
            {tokenSource === 'env' && (
              <span className="text-xs bg-yellow-50 text-yellow-700 font-medium px-2 py-0.5 rounded-lg">Lido do servidor (.env)</span>
            )}
          </div>
        </div>

        {/* Expiry timer */}
        {daysInfo !== null && (
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500 font-medium">Validade do token</span>
              <span className={`font-bold ${daysInfo.daysLeft <= 7 ? 'text-red-600' : daysInfo.daysLeft <= 20 ? 'text-yellow-600' : 'text-green-600'}`}>
                {daysInfo.daysLeft <= 0
                  ? 'Expirado — atualize agora!'
                  : `${daysInfo.daysLeft} dia${daysInfo.daysLeft === 1 ? '' : 's'} restante${daysInfo.daysLeft === 1 ? '' : 's'}`}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${daysInfo.pct > 33 ? 'bg-green-500' : daysInfo.pct > 12 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${daysInfo.pct}%` }}
              />
            </div>
            <p className="text-xs text-gray-400">
              Atualizado há {daysInfo.daysSince} dia{daysInfo.daysSince === 1 ? '' : 's'} · expira em {TOKEN_EXPIRY_DAYS} dias
            </p>
          </div>
        )}

        <p className="text-xs text-gray-400">
          O token expira a cada {TOKEN_EXPIRY_DAYS} dias. Cole o novo token abaixo para atualizar sem precisar acessar o servidor.
        </p>

        <div className="space-y-2">
          <div className="relative">
            <input
              type={tokenVisible ? 'text' : 'password'}
              value={tokenLoading ? '' : token}
              onChange={e => setToken(e.target.value)}
              placeholder={tokenLoading ? 'Carregando…' : 'Cole o token aqui…'}
              disabled={tokenLoading}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={() => setTokenVisible(v => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {tokenVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button
            onClick={handleTokenSave}
            disabled={tokenSaving || tokenLoading || !token.trim()}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            {tokenSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Atualizar token
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 7. FAQ ────────────────────────────────────────────────────────────────────

function FaqContent() {
  const { getText, upsert, loading } = usePageContent('home');
  const { toast } = useToast();

  const [label, setLabel]       = useState('');
  const [title, setTitle]       = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (loading) return;
    setLabel(getText('faq', 'label', 'Dúvidas Frequentes'));
    setTitle(getText('faq', 'title', 'Perguntas Frequentes'));
    setSubtitle(getText('faq', 'subtitle', 'Tire suas dúvidas sobre os atendimentos'));
  }, [loading]);

  const origLabel    = getText('faq', 'label',    'Dúvidas Frequentes');
  const origTitle    = getText('faq', 'title',    'Perguntas Frequentes');
  const origSubtitle = getText('faq', 'subtitle', 'Tire suas dúvidas sobre os atendimentos');
  const dirty = label !== origLabel || title !== origTitle || subtitle !== origSubtitle;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('faq', 'label',    { text: label }),
        upsert('faq', 'title',    { text: title }),
        upsert('faq', 'subtitle', { text: subtitle }),
      ]);
      toast({ title: 'FAQ salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cabeçalho da seção</p>
        <VisibilityToggle visKey={PAGE_VISIBILITY_KEYS.faq} label="Seção" />
      </div>
      <Field label="Rótulo (acima do título)">
        <TextInput value={label} onChange={setLabel} placeholder="Dúvidas Frequentes" maxLength={60} />
      </Field>
      <Field label="Título principal">
        <TextInput value={title} onChange={setTitle} placeholder="Perguntas Frequentes" maxLength={100} />
      </Field>
      <Field label="Subtítulo">
        <TextInput value={subtitle} onChange={setSubtitle} placeholder="Tire suas dúvidas sobre os atendimentos" maxLength={200} />
      </Field>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />

      <div className="border-t border-gray-100 pt-4">
        <FAQManager />
      </div>
    </div>
  );
}

// ── 8. Depoimentos ────────────────────────────────────────────────────────────

function DepoimentosContent() {
  const { getText, upsert, loading } = usePageContent('home');
  const { toast } = useToast();

  const [label, setLabel] = useState('');
  const [title, setTitle] = useState('');
  const [googleReviewsRating, setGoogleReviewsRating] = useState('');
  const [googleReviewsLabel, setGoogleReviewsLabel] = useState('');
  const [googleReviewsUrl, setGoogleReviewsUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    setLabel(getText('testimonials', 'label', 'Resultados reais'));
    setTitle(getText('testimonials', 'title', 'O que dizem meus pacientes'));
    setGoogleReviewsRating(getText('testimonials', 'google_reviews_rating', '5.0'));
    setGoogleReviewsLabel(getText('testimonials', 'google_reviews_label', '100+ avaliações no Google'));
    setGoogleReviewsUrl(getText('testimonials', 'google_reviews_url', ''));
  }, [loading]);

  const origLabel = getText('testimonials', 'label', 'Resultados reais');
  const origTitle = getText('testimonials', 'title', 'O que dizem meus pacientes');
  const origGoogleReviewsRating = getText('testimonials', 'google_reviews_rating', '5.0');
  const origGoogleReviewsLabel = getText('testimonials', 'google_reviews_label', '100+ avaliações no Google');
  const origGoogleReviewsUrl = getText('testimonials', 'google_reviews_url', '');
  const dirty = label !== origLabel || title !== origTitle || googleReviewsRating !== origGoogleReviewsRating || googleReviewsLabel !== origGoogleReviewsLabel || googleReviewsUrl !== origGoogleReviewsUrl;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('testimonials', 'label', { text: label }),
        upsert('testimonials', 'title', { text: title }),
        upsert('testimonials', 'google_reviews_rating', { text: googleReviewsRating }),
        upsert('testimonials', 'google_reviews_label', { text: googleReviewsLabel }),
        upsert('testimonials', 'google_reviews_url', { text: googleReviewsUrl.trim() }),
      ]);
      toast({ title: 'Depoimentos salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cabeçalho da seção</p>
        <VisibilityToggle visKey={PAGE_VISIBILITY_KEYS.depoimentos} label="Seção" />
      </div>
      <Field label="Rótulo (acima do título)">
        <TextInput value={label} onChange={setLabel} placeholder="Resultados reais" maxLength={60} />
      </Field>
      <Field label="Título principal">
        <TextInput value={title} onChange={setTitle} placeholder="O que dizem meus pacientes" maxLength={100} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Nota no Google">
          <TextInput value={googleReviewsRating} onChange={setGoogleReviewsRating} placeholder="5.0" maxLength={8} />
        </Field>
        <Field label="Texto das avaliações do Google">
          <TextInput value={googleReviewsLabel} onChange={setGoogleReviewsLabel} placeholder="100+ avaliações no Google" maxLength={60} />
        </Field>
        <Field label="Link do consultório no Google" hint="Cole o link público do perfil ou das avaliações do consultório.">
          <input type="url" value={googleReviewsUrl} onChange={e => setGoogleReviewsUrl(e.target.value)} placeholder="https://g.page/..." maxLength={500}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
        </Field>
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />

      <div className="border-t border-gray-100 pt-4 space-y-3">
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <span className="text-amber-500 text-sm shrink-0">⚠️</span>
          <p className="text-xs text-amber-700 leading-relaxed">
            O carrossel exibe no máximo <strong>6 depoimentos</strong>. Cadastros além desse limite não serão exibidos no site público.
          </p>
        </div>
        <TestimonialsManager />
      </div>
    </div>
  );
}

// ── 9. CTA ────────────────────────────────────────────────────────────────────

function CTAContent() {
  const { getText, get, upsert, loading } = usePageContent('home');
  const { toast } = useToast();

  const [tagline, setTagline]   = useState('');
  const [title1, setTitle1]     = useState('');
  const [title2, setTitle2]     = useState('');
  const [desc, setDesc]         = useState('');
  const [btnPrimary, setBtnP]           = useState('');
  const [btnPrimaryUrl, setBtnPrimaryUrl] = useState('');
  const [btnWpp, setBtnWpp]             = useState('');
  const [wppUrl, setWppUrl]             = useState('');
  const [stats, setStats]               = useState<Array<{value: string; label: string}>>([]);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (loading) return;
    setTagline(getText('cta', 'tagline', 'Sua transformação começa agora'));
    setTitle1(getText('cta', 'title_1', 'Chega de dietas'));
    setTitle2(getText('cta', 'title_2', 'que não funcionam.'));
    setDesc(getText('cta', 'description', 'Agende sua avaliação gratuita e descubra o plano alimentar feito para o seu corpo e a sua rotina.'));
    setBtnP(getText('cta', 'btn_primary', 'Agendar Consulta Gratuita'));
    setBtnPrimaryUrl(getText('cta', 'btn_primary_url', 'https://calendar.app.google/ULCXQLbU7iFtuVHh7'));
    setBtnWpp(getText('cta', 'btn_whatsapp', 'WhatsApp'));
    setWppUrl(getText('cta', 'whatsapp_url', 'https://wa.me/5511998874081'));
    setStats((get('cta', 'stats')?.content_value?.items as any[]) ?? [
      { value: '+500', label: 'Pacientes atendidos' },
      { value: '6 anos', label: 'De experiência' },
      { value: '3', label: 'Consultórios' },
      { value: '5.0 ★', label: 'No Google' },
    ]);
  }, [loading]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('cta', 'tagline', { text: tagline }),
        upsert('cta', 'title_1', { text: title1 }),
        upsert('cta', 'title_2', { text: title2 }),
        upsert('cta', 'description', { text: desc }),
        upsert('cta', 'btn_primary', { text: btnPrimary }),
        upsert('cta', 'btn_primary_url', { text: btnPrimaryUrl }),
        upsert('cta', 'btn_whatsapp', { text: btnWpp }),
        upsert('cta', 'whatsapp_url', { text: wppUrl }),
        upsert('cta', 'stats', { items: stats }),
      ]);
      toast({ title: 'CTA salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const updateStat = (i: number, field: 'value' | 'label', val: string) =>
    setStats(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  return (
    <div className="space-y-4">
      <Field label="Rótulo"><TextInput value={tagline} onChange={setTagline} placeholder="Sua transformação começa agora" maxLength={80} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Título linha 1 (itálico)"><TextInput value={title1} onChange={setTitle1} placeholder="Chega de dietas" maxLength={60} /></Field>
        <Field label="Título linha 2 (negrito)"><TextInput value={title2} onChange={setTitle2} placeholder="que não funcionam." maxLength={60} /></Field>
      </div>
      <Field label={`Descrição (${desc.length}/300)`}><TextArea value={desc} onChange={setDesc} rows={3} maxLength={300} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Texto botão principal"><TextInput value={btnPrimary} onChange={setBtnP} maxLength={60} /></Field>
        <Field label="Link do botão principal">
          <TextInput value={btnPrimaryUrl} onChange={setBtnPrimaryUrl} placeholder="https://calendar.app.google/..." maxLength={300} />
        </Field>
        <Field label="Texto botão WhatsApp"><TextInput value={btnWpp} onChange={setBtnWpp} maxLength={40} /></Field>
        <Field label="URL WhatsApp"><TextInput value={wppUrl} onChange={setWppUrl} maxLength={100} /></Field>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Estatísticas (4 números)</p>
        <div className="grid grid-cols-2 gap-3">
          {stats.map((stat, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-2">
              <Field label={`Valor ${i+1}`}><TextInput value={stat.value} onChange={v => updateStat(i, 'value', v)} placeholder="+500" maxLength={20} /></Field>
              <Field label="Rótulo"><TextInput value={stat.label} onChange={v => updateStat(i, 'label', v)} placeholder="Pacientes atendidos" maxLength={30} /></Field>
            </div>
          ))}
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Salvar CTA
      </button>
    </div>
  );
}

// ── Main HomeManager ──────────────────────────────────────────────────────────

type SectionId = 'hero' | 'parceiros' | 'jornada' | 'diferenciais' | 'blog' | 'instagram' | 'faq' | 'depoimentos' | 'cta';

const FIXED_SECTIONS: Array<{ id: SectionId; label: string; icon: React.ElementType; description: string }> = [
  { id: 'hero',      label: 'Hero',      icon: Home,   description: 'Banner principal com foto, título e botões' },
  { id: 'parceiros', label: 'Parceiros', icon: Users2, description: 'Carrossel de logos de parceiros/patrocinadores' },
];

const REORDERABLE_SECTIONS: Array<{ id: SectionId; label: string; icon: React.ElementType; description: string }> = [
  { id: 'jornada',      label: 'Jornada',      icon: Route,               description: 'Etapas do atendimento (passo a passo)' },
  { id: 'diferenciais', label: 'Diferenciais', icon: LayoutGrid,          description: 'Cards com os diferenciais da abordagem' },
  { id: 'blog',         label: 'Blog',         icon: BookMarked,          description: 'Carrossel com os últimos artigos' },
  { id: 'instagram',    label: 'Instagram',    icon: Camera,              description: 'Feed com os últimos posts do Instagram' },
  { id: 'faq',          label: 'FAQ',          icon: HelpCircle,          description: 'Perguntas e respostas frequentes' },
  { id: 'depoimentos',  label: 'Depoimentos',  icon: MessageSquareQuote,  description: 'Depoimentos de pacientes' },
  { id: 'cta',          label: 'CTA',          icon: Sparkles,            description: 'Chamada para ação no final da página' },
];

const DEFAULT_ORDER = REORDERABLE_SECTIONS.map(s => s.id);

function renderContent(id: SectionId) {
  switch (id) {
    case 'hero':         return <HeroContent />;
    case 'parceiros':    return <PatrocinadoresContent />;
    case 'jornada':      return <JornadaContent />;
    case 'diferenciais': return <DiferenciaisContent />;
    case 'blog':         return <BlogContent />;
    case 'instagram':    return <InstagramContent />;
    case 'faq':          return <FaqContent />;
    case 'depoimentos':  return <DepoimentosContent />;
    case 'cta':          return <CTAContent />;
  }
}

export function HomeManager() {
  const { toast } = useToast();
  const [openSection, setOpenSection] = useState<SectionId>('hero');
  const [order, setOrder] = useState<SectionId[]>(DEFAULT_ORDER);

  useEffect(() => {
    settingsApi.list().then(settings => {
      const s = settings.find(s => s.key === 'home_sections_order');
      if (s?.value) {
        try {
          const saved: SectionId[] = JSON.parse(s.value);
          // Merge: keep saved order, append any new sections not yet in saved list
          const merged = [
            ...saved.filter(id => DEFAULT_ORDER.includes(id)),
            ...DEFAULT_ORDER.filter(id => !saved.includes(id)),
          ];
          setOrder(merged);
        } catch {}
      }
    });
  }, []);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const newOrder = arrayMove(order, order.indexOf(active.id as SectionId), order.indexOf(over.id as SectionId));
    setOrder(newOrder);
    try {
      await settingsApi.update('home_sections_order', JSON.stringify(newOrder));
    } catch {
      toast({ title: 'Erro ao salvar ordem', variant: 'destructive' });
    }
  };

  const toggle = (id: SectionId) => setOpenSection(prev => prev === id ? ('' as SectionId) : id);

  const allSections = [...FIXED_SECTIONS, ...order.map(id => REORDERABLE_SECTIONS.find(s => s.id === id)!).filter(Boolean)];

  return (
    <div className="space-y-3">
      {/* Quick jump nav */}
      <div className="flex flex-wrap gap-1.5 bg-white border border-gray-100 rounded-xl p-2.5">
        {allSections.map(s => (
          <button key={s.id} onClick={() => {
            setOpenSection(s.id);
            setTimeout(() => document.getElementById(`home-sec-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
          }}
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${openSection === s.id ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-brand-primary/10 hover:text-brand-primary'}`}>
            <s.icon className="w-3 h-3 shrink-0" />
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {/* Fixed sections — not draggable */}
      {FIXED_SECTIONS.map(s => (
        <SectionBlock key={s.id} id={s.id} icon={s.icon} title={s.label} description={s.description}
          isOpen={openSection === s.id} onToggle={() => toggle(s.id)}>
          {renderContent(s.id)}
        </SectionBlock>
      ))}

      {/* Reorderable sections — drag by grip handle */}
      <p className="text-[11px] text-gray-400 flex items-center gap-1 px-1">
        <GripVertical className="w-3.5 h-3.5" />
        Arraste pelo ícone para reordenar as seções abaixo
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {order.map(id => {
              const s = REORDERABLE_SECTIONS.find(r => r.id === id)!;
              if (!s) return null;
              return (
                <SortableSectionBlock key={s.id} id={s.id} icon={s.icon} title={s.label} description={s.description}
                  isOpen={openSection === s.id} onToggle={() => toggle(s.id)}>
                  {renderContent(s.id)}
                </SortableSectionBlock>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
